// src/errors/vy-base-error.ts
var VyBaseError = class extends Error {
  code;
  metadata;
  originalError;
  constructor(input) {
    super(input.message);
    this.name = "VyBaseError";
    this.code = input.code;
    this.metadata = input.metadata;
    this.originalError = input.cause;
    Error.captureStackTrace(this, this.constructor);
  }
};

// src/errors/unreachable-value-error.ts
var UnreachableValueError = class extends VyBaseError {
  constructor(nameOfUnreachableValue, code) {
    super({
      code,
      message: `Unreachable value: ${nameOfUnreachableValue}`,
      metadata: {
        nameOfUnreachableValue
      }
    });
  }
};

// src/errors/unsupported-value-error.ts
var UnsupportedValueError = class extends VyBaseError {
  constructor(nameOfUnsupportedValue, code) {
    super({
      code,
      message: `Unsupported value: ${nameOfUnsupportedValue}`,
      metadata: {
        nameOfUnsupportedValue
      }
    });
  }
};

// src/errors/not-found-error.ts
var NotFoundError = class extends VyBaseError {
  constructor(notFoundEntityName, searchParams, code) {
    const metadata = {
      notFoundEntityName,
      searchParams
    };
    super({
      code,
      message: `Entity not found: ${notFoundEntityName}`,
      metadata
    });
  }
};

// src/errors/forbidden-error.ts
var ForbiddenError = class extends VyBaseError {
  constructor(message, code, metadata) {
    super({
      code,
      message,
      metadata
    });
  }
};

// src/errors/authentication-error.ts
var AuthenticationError = class extends VyBaseError {
  constructor(entityName, code, cause) {
    const metadata = {
      entityName
    };
    super({
      code,
      message: `Authentication failed for ${entityName}`,
      metadata,
      cause
    });
  }
};

// src/errors/public-safe-error.ts
var PublicSafeError = class extends VyBaseError {
  details;
  headers;
};

// src/services/raise-if-not-error-type.ts
function raiseIfNotErrorType(error, errorType) {
  const isOfErrorType = error instanceof errorType;
  if (!isOfErrorType) {
    throw error;
  }
}

// src/services/typed-catch.ts
var NO_ERROR = void 0;
function typedCatch(fn, expectedError) {
  try {
    const result = fn();
    return { value: result, success: true };
  } catch (error) {
    raiseIfNotErrorType(error, expectedError);
    return { error, success: false };
  }
}
async function typedCatchAsync(promiseToCatch, expectedError, refineResult) {
  try {
    const result = await promiseToCatch;
    if (refineResult) {
      await refineResult(result);
    }
    return { value: result, success: true };
  } catch (error) {
    if (expectedError) {
      raiseIfNotErrorType(error, expectedError);
    }
    return { error, success: false };
  }
}

// src/services/catch-any.ts
function catchAny(fn) {
  return typedCatch(fn, Error);
}
function catchAnyAsync(promiseToCatch) {
  return typedCatchAsync(promiseToCatch, Error);
}

// src/services/catch-with-default.ts
function catchWithDefault(fn, defaultValue, expectedError) {
  const result = typedCatch(fn, expectedError);
  return result.success ? result.value : defaultValue;
}
function catchWithDefaultAny(fn, defaultValue) {
  return catchWithDefault(fn, defaultValue, Error);
}
async function catchWithDefaultAsync(promiseToCatch, defaultValue, expectedError) {
  const result = await typedCatchAsync(promiseToCatch, expectedError);
  return result.success ? result.value : defaultValue;
}
async function catchWithDefaultAnyAsync(promiseToCatch, defaultValue) {
  return catchWithDefaultAsync(promiseToCatch, defaultValue, Error);
}

// src/services/raise.ts
function raise(error) {
  throw error;
}

// src/services/raise-if.ts
function raiseIf(condition, error) {
  if (condition) {
    throw error;
  }
}

// src/services/raise-unreachable.ts
function raiseUnreachable(value, UnreachableErrorToThrow) {
  raise(UnreachableErrorToThrow);
}

// src/services/raise-unsupported.ts
function raiseUnsupported(value, UnsupportedErrorToThrow) {
  raise(UnsupportedErrorToThrow);
}

// src/services/catch-transform.ts
function catchTransformAny(fn, transform) {
  return catchTransform(fn, Error, transform);
}
function catchTransformAnyAsync(promiseToCatch, transform) {
  return catchTransformAsync(promiseToCatch, Error, transform);
}
function catchTransform(fn, expectedError, transform) {
  const fnResult = typedCatch(fn, expectedError);
  if (fnResult.success) {
    return fnResult.value;
  }
  const transformedError = transform(fnResult.error);
  raise(transformedError);
}
async function catchTransformAsync(promiseToCatch, expectedError, transform) {
  const fnResult = await typedCatchAsync(promiseToCatch, expectedError);
  if (fnResult.success) {
    return fnResult.value;
  }
  const transformedError = transform(fnResult.error);
  raise(transformedError);
}

// src/services/is-vy-base-error.ts
function isVyBaseError(error) {
  return error instanceof VyBaseError;
}

// src/services/to-error.ts
function toError(value) {
  return value instanceof Error ? value : new Error(String(value));
}

// src/services/raise-if-not-found.ts
function raiseIfNotFound(value, UnreachableErrorToThrow) {
  if (value === void 0 || value === null) {
    return raise(UnreachableErrorToThrow);
  }
}
export {
  AuthenticationError,
  ForbiddenError,
  NO_ERROR,
  NotFoundError,
  PublicSafeError,
  UnreachableValueError,
  UnsupportedValueError,
  VyBaseError,
  catchAny,
  catchAnyAsync,
  catchTransform,
  catchTransformAny,
  catchTransformAnyAsync,
  catchTransformAsync,
  catchWithDefault,
  catchWithDefaultAny,
  catchWithDefaultAnyAsync,
  catchWithDefaultAsync,
  isVyBaseError,
  raise,
  raiseIf,
  raiseIfNotErrorType,
  raiseIfNotFound,
  raiseUnreachable,
  raiseUnsupported,
  toError,
  typedCatch,
  typedCatchAsync
};
